import RegUIClient, {
  CaptchaType,
  InternalError,
  RegistrationClientResponse,
  RegUIClientBconnectLoginResponse,
  RegUIClientSocialLoginResponse,
  VerifyVerificationCodeResponse,
} from 'reg-ui-client';

import { GoogleResponse } from '../GoogleSignInButton';
import defaultMessages from '../i18n/defaultMessages';

export const SOCIAL_SITES = {
  GOOGLE: 'GOOGLE',
  APPLE: 'APPLE',
  FACEBOOK: 'FACEBOOK',
  TWITTER: 'TWITTER',
  TERMINAL: 'TERMINAL',
};

export type SupportedSocialsType = keyof typeof SOCIAL_SITES;

export const onSocialLogIn = async (
  regUIClient: RegUIClient | undefined,
  socialSite: SupportedSocialsType,
  marketingConsent?: boolean,
  leadSource = 'SocialUser'
): Promise<RegUIClientSocialLoginResponse> => {
  if (!regUIClient) {
    throw new Error('RegUIClient undefined!');
  }

  const errorMessage = defaultMessages[
    'media-ui-login_unknownSocialSite'
  ].replace('{socialSite}', socialSite);
  let userDataResp: RegUIClientSocialLoginResponse = {
    success: false,
    data: {},
    error: {
      errorMessage,
    } as InternalError,
  };

  switch (socialSite) {
    case SOCIAL_SITES.APPLE:
      /* c8 ignore next */
      userDataResp = await regUIClient.appleLogin(leadSource, marketingConsent);
      break;
    case SOCIAL_SITES.FACEBOOK:
      /* c8 ignore next */
      userDataResp = await regUIClient.facebookLogin(
        leadSource,
        marketingConsent
      );
      break;
    case SOCIAL_SITES.TERMINAL:
      /* c8 ignore next */
      userDataResp = await regUIClient.terminalLogin(
        leadSource,
        marketingConsent
      );
      break;
    case SOCIAL_SITES.TWITTER:
      /* c8 ignore next */
      userDataResp = await regUIClient.twitterLogin(
        leadSource,
        marketingConsent
      );
      break;
    default:
      // eslint-disable-next-line no-console
      console.error(errorMessage);
      break;
  }
  // Note: the social sign ins already call sendEmailToEloquaAndCpref in regUIClient

  return userDataResp;
};

export const onVerificationCodeInput = async (
  regUIClient: RegUIClient | undefined,
  email: string,
  code: string,
  marketingConsent: boolean,
  leadSource = 'CreateAccountUser'
): Promise<
  | undefined
  | RegistrationClientResponse<VerifyVerificationCodeResponse | undefined>
> => {
  /* c8 ignore next */
  const userDataResp = await regUIClient?.verifyVerificationCode(
    email,
    code,
    leadSource,
    marketingConsent
  );

  // Note: verifyVerificationCode already calls sendEmailToEloquaAndCpref in regUIClient

  return userDataResp;
};

export const onEmailLogin = async (
  regUIClient: RegUIClient | undefined,
  email: string
): Promise<undefined | RegUIClientSocialLoginResponse> => {
  let userDataResp: undefined | RegUIClientSocialLoginResponse;

  if (email.endsWith('@bloomberg.net')) {
    /* c8 ignore next */
    regUIClient?.linkToBSSO();

    /* c8 ignore next */
    userDataResp = await regUIClient?.terminalLogin();
  } else {
    /* c8 ignore next */
    const isEligibleForSSO =
      await regUIClient?.redirectToGeneratedGroupSubsSSOUrlIfEligible(email);

    if (isEligibleForSSO) {
      // Is redirecting to Group Subs SSO
      userDataResp = {
        data: {
          isGroupSSO: true,
        },
      } as RegUIClientSocialLoginResponse;
    }
  }

  return userDataResp;
};

export const onVerificationCodeRequest = (
  regUIClient: RegUIClient | undefined,
  email: string
):
  | undefined
  | Promise<
      RegistrationClientResponse<Record<string, unknown> | undefined>
    > => {
  /* c8 ignore next */
  return regUIClient?.sendVerificationEmail(email);
};

export const onGoogleSignIn = async (
  regUIClient: RegUIClient | undefined,
  googleResponse: GoogleResponse,
  marketingConsent?: boolean
): Promise<RegUIClientSocialLoginResponse> => {
  try {
    if (!regUIClient) {
      throw new Error('RegUIClient undefined');
    }

    /* c8 ignore next */
    const userDataResp: RegUIClientSocialLoginResponse | undefined =
      await regUIClient.googleSignIn(
        googleResponse.credential,
        marketingConsent
      );

    /* c8 ignore next 3 */
    // eslint-disable-next-line no-console
    console.log(
      'info',
      'Response from Google Sign In:',
      JSON.stringify(userDataResp)
    );
    // Note: the google sign in already calls sendEmailToEloquaAndCpref in regUIClient

    return userDataResp;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Something went wrong =>', err);
  }

  return { success: false, data: {} };
};

export const onBconnectSignIn = async (
  regUIClient: RegUIClient | undefined,
  email: string,
  password: string,
  captcha?: CaptchaType
): Promise<RegUIClientBconnectLoginResponse> => {
  try {
    if (!regUIClient) {
      throw new Error('RegUIClient undefined');
    }

    /* c8 ignore next */
    const userDataResp = await regUIClient.passwordLogin(
      email,
      password,
      captcha
    );

    // eslint-disable-next-line no-console
    console.log(
      'info',
      'Response from BConnect Sign In:',
      JSON.stringify(userDataResp)
    );
    // Note: the google sign in already calls sendEmailToEloquaAndCpref in regUIClient

    return userDataResp;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Something went wrong =>', err);
  }

  return {
    success: false,
    error: {
      errorMessage: defaultMessages['media-ui-login_failedToAuthenticate'],
      name: 'AUTHENTICATION_ERROR',
      message: defaultMessages['media-ui-login_failedToAuthenticate'],
    },
    data: {},
  };
};
